import type { TablePagination } from '@lifebit-ai/lds'
import { useCallback, useMemo, useState } from 'react'

import { useToggleState } from 'hooks/useToggleState'
import {
  type DataAccessRequestsTableFiltersState,
  buildDataAccessRequestsVerificationParams,
  defaultDataAccessRequestsTableFiltersState,
  useDataAccessRequestsTable
} from 'modules/requests/data-access-requests/hooks/useDataAccessRequestsTable'
import type { Option } from 'modules/types'
import { DEFAULT_PAGE_SIZE } from 'services/results'

const countActiveDataAccessRequestFilters = (filtersState: DataAccessRequestsTableFiltersState): number =>
  Object.values(filtersState).filter(value =>
    Array.isArray(value) ? value.length > 0 : typeof value === 'string' && value.length > 0
  ).length

export const useDataAccessRequestsTablePage = () => {
  const [tableState, setTableState] = useState<{ page: number; pageSize: number }>({
    page: 0,
    pageSize: DEFAULT_PAGE_SIZE
  })
  const [filtersState, setFiltersState] = useState(defaultDataAccessRequestsTableFiltersState)
  const [areFiltersVisible, toggleFiltersVisibility] = useToggleState(true)

  const queryParams = useMemo(
    () => buildDataAccessRequestsVerificationParams(tableState, filtersState),
    [filtersState, tableState]
  )

  const { columns, dataAccessRequests, openDataAccessRequest, isLoading, pagination, showPagination } =
    useDataAccessRequestsTable(queryParams)

  const activeFiltersCount = useMemo(() => countActiveDataAccessRequestFilters(filtersState), [filtersState])

  const resetPage = useCallback(() => {
    setTableState(prev => ({ ...prev, page: 0 }))
  }, [])

  const applyCheckboxFilter = useCallback(
    (
        filterKey: keyof Pick<
          DataAccessRequestsTableFiltersState,
          'status' | 'labelIds' | 'workspaceIds' | 'requesterIds' | 'assigneeIds'
        >
      ) =>
      (filteredOptions: Option[]) => {
        setFiltersState(prev => ({
          ...prev,
          [filterKey]: filteredOptions.map(option => option.value)
        }))
        resetPage()
      },
    [resetPage]
  )

  const resetCheckboxFilter = useCallback(
    (
        filterKey: keyof Pick<
          DataAccessRequestsTableFiltersState,
          'status' | 'labelIds' | 'workspaceIds' | 'requesterIds' | 'assigneeIds'
        >
      ) =>
      () => {
        setFiltersState(prev => ({ ...prev, [filterKey]: [] }))
        resetPage()
      },
    [resetPage]
  )

  const applySearchFilter = useCallback(
    (searchTerm: string) => {
      setFiltersState(prev => ({ ...prev, search: searchTerm.trim() }))
      resetPage()
    },
    [resetPage]
  )

  const resetSearchFilter = useCallback(() => {
    setFiltersState(prev => ({ ...prev, search: '' }))
    resetPage()
  }, [resetPage])

  const tablePagination: TablePagination = useMemo(
    () => ({
      ...pagination,
      onPageChange: (page: number) => {
        setTableState(prev => ({ ...prev, page }))
      },
      onPageSizeChange: (pageSize: number) => {
        setTableState(prev => ({ ...prev, page: 0, pageSize }))
      }
    }),
    [pagination]
  )

  return {
    activeFiltersCount,
    applyCheckboxFilter,
    applySearchFilter,
    areFiltersVisible,
    columns,
    dataAccessRequests,
    isLoading,
    openDataAccessRequest,
    resetCheckboxFilter,
    resetSearchFilter,
    showPagination,
    tablePagination,
    toggleFiltersVisibility
  }
}
